import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AlertTriangle, Save, X } from "lucide-react";

import { Modal } from "./Index";
import { TextInput } from "../shared/Inputs";
import { ISelectOption, Select } from "../shared/Selects";
import { areEntitlementsEqual, isEntitlementsInvalid, ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { closeModal, getModalData, isModalActive } from "../../store/modal.slice";

import { useGetCohort, useSaveCohort } from "../../hooks/cohorts-cax";
import { useGetSubClients } from "../../hooks/sub_clients";
import { useGetEntitlementsForAdminForm } from "../../hooks/entitlements";

import { IEntitlementsData, ISaveEntitlementFromAdminPortalsReqData } from "../../types/misc.interface";
import { EEntitlementSource, EProduct } from "../../types/misc.enum";

const MODAL_NAME = "MANAGE_COHORT_MODAL";

export const ManageCohortModal = () => {
    const dispatch = useDispatch();

    const isModalOpen = useSelector(isModalActive(MODAL_NAME));
    const modalData = useSelector(getModalData(MODAL_NAME));

    const [name, setName] = useState<string>("");
    const [selectedSubClient, setSelectedSubClient] = useState<ISelectOption>({ value: null, label: "-- Select a sub client --" });
    const [currentEntitlements, setCurrentEntitlements] = useState<IEntitlementsData>({
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { data: cohortDetails } = useGetCohort(modalData?.cohortId ?? null);
    const { data: subClients } = useGetSubClients(isModalOpen);
    const { mutate: saveCohort } = useSaveCohort();
    const { data: availableEntitlements } = useGetEntitlementsForAdminForm(cohortDetails?.id ? cohortDetails.id.toString() : null, EEntitlementSource.COHORT);

    const selectedSubClientId = selectedSubClient.value ? Number(selectedSubClient.value) : null;
    const isSubClientChanged = !!cohortDetails?.id && selectedSubClientId !== (cohortDetails.sub_client_id ?? null);

    const onCloseModal = () => {
        dispatch(closeModal(MODAL_NAME));
    };

    const resetForm = () => {
        setName("");
        setSelectedSubClient({ value: null, label: "-- Select a sub client --" });
        setCurrentEntitlements({
            products: [],
            mbts_modules: [],
            mcsa_modules: [],
            mbts_assessments: 0,
            mcsa_assessments: 0,
        });
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    useEffect(() => {
        if (cohortDetails) {
            setName(cohortDetails.name ?? "");

            const subClient = subClients?.find(sc => sc.id === cohortDetails.sub_client_id);
            if (subClient) {
                setSelectedSubClient({ value: subClient.id.toString(), label: subClient.name });
            } else {
                setSelectedSubClient({ value: null, label: "-- Select a sub client --" });
            };
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortDetails, subClients]);

    const isDataSame = () => {
        // Entitlement caps depend on sub client vs client; ignore entitlement edits while sub client is changing.
        const isEntitlementsSame = isSubClientChanged || areEntitlementsEqual(currentEntitlements, cohortDetails?.entitlements);
        const isSubClientSame = selectedSubClientId === (cohortDetails?.sub_client_id ?? null);

        if (!!cohortDetails?.id) {
            return (
                name === cohortDetails?.name
                && isSubClientSame
                && isEntitlementsSame
            );
        };

        return name === cohortDetails?.name && isSubClientSame && isEntitlementsSame;
    };

    const isFormFilled = () => {
        return name.trim() !== "";
    };

    const handleSave = () => {
        const entitlementsInvalid = !isSubClientChanged && isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null);
        if (isProcessing || !isFormFilled() || isDataSame() || entitlementsInvalid) {
            return;
        };

        // Parent pool (client vs sub client) changed — reset entitlements; backend enforces the same.
        let entitlements: ISaveEntitlementFromAdminPortalsReqData;
        if (isSubClientChanged) {
            entitlements = {
                products: [],
                mbts_modules: [],
                mcsa_modules: [],
                mbts_assessments: 0,
                mcsa_assessments: 0,
            };
        } else {
            const products = currentEntitlements.products.map(product => product.key);
            entitlements = {
                products,
                mbts_modules: products.includes(EProduct.MBTS) ? currentEntitlements.mbts_modules.map(module => module.id) : [],
                mcsa_modules: products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_modules.map(module => module.id) : [],
                mbts_assessments: products.includes(EProduct.MBTS) ? currentEntitlements.mbts_assessments : 0,
                mcsa_assessments: products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_assessments : 0,
            };
        };

        saveCohort({
            payload: {
                id: modalData?.cohortId ?? null,
                name,
                sub_client_id: selectedSubClientId,
                entitlements,
            },
            setIsProcessing,
        });
    };

    return (
        <Modal
            name={MODAL_NAME}
            title="Manage Cohort"
            baseClass="manage_cohort"
            topupLvl={0}
            onClose={onCloseModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
        >
            <button className="modal__close_btn" onClick={onCloseModal}><X /></button>

            <p className="color--primary text-xl text-bold mb-2xl">
                {modalData?.cohortId ? 'Update' : 'Add'} cohort
            </p>

            <div className="modal__cohort_form">
                <div className="modal__cohort_form__block">
                    <div className="modal__cohort_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Name</p>
                    </div>

                    <div className="modal__cohort_form__block__right">
                        <div className="d-flex gap-xl">
                            <TextInput
                                placeholder="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal__cohort_form__block">
                    <div className="modal__cohort_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Sub Client</p>
                    </div>

                    <div className="modal__cohort_form__block__right">
                        <Select
                            fluid
                            disabled={isProcessing}
                            selected={selectedSubClient}
                            options={[
                                { value: null, label: "-- Select a sub client --" },
                                ...(subClients?.map(subClient => ({
                                    value: subClient.id.toString(),
                                    label: subClient.name,
                                })) ?? []),
                            ]}
                            onSelect={option => setSelectedSubClient(option)}
                        />
                    </div>
                </div>
                {!!cohortDetails?.id && !!availableEntitlements?.maximum_caps?.products?.length && (
                    <Fragment>
                        {isSubClientChanged && (
                            <div className="modal__cohort_form__entitlements_warning">
                                <AlertTriangle />
                                <p className="text-sm">
                                    Changing the parent pool will reset all entitlements to match the selected parent. Any unsaved entitlement changes will be lost. You can edit the entitlements again after saving.
                                    Along with the entitlements, all associated members entitlements will be reset as well.
                                </p>
                            </div>
                        )}
                        <ModalEntitlementForm
                            isModalOpen={isModalOpen}
                            source={EEntitlementSource.COHORT}
                            availableEntitlements={availableEntitlements}
                            entitlements={cohortDetails?.entitlements ?? null}
                            onEntitlementsChange={setCurrentEntitlements}
                            isProcessing={isProcessing || isSubClientChanged}
                            assessmentsTooltip="Maximum number of {PRODUCT_NAME} assessments allowed to be distributed between beneficiaries (Users)."
                        />
                    </Fragment>
                )}
                <div className="modal__cohort_form__actions mt-xl">
                    <button
                        className="btn btn--sm btn--radius-sm btn--primary"
                        onClick={handleSave}
                        disabled={!isFormFilled() || isDataSame() || (!isSubClientChanged && isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null))}
                    >
                        <Save />
                        <span>{isProcessing ? 'Saving...' : 'Save'}</span>
                        {isProcessing && <span className="btn-loader" />}
                    </button>
                </div>
            </div>
        </Modal>
    );
};